/**
 * @fileoverview AI assistance panel for template canvas field suggestions.
 * @module template-builder/CanvasAIPanel
 *
 * Responsibilities:
 * - Render AI prompt and document-upload suggestion workflows.
 * - Apply selected suggestions to host-owned forms.
 * - Defer online AI work to host-injected handlers instead of direct API calls.
 *
 * Dependencies:
 * - React state hooks.
 * - Template type contracts.
 * - AI suggestion field resolver.
 *
 * Architecture:
 * - Shared UI component only; no direct fetch or apiClient calls are made here.
 * - Host apps decide whether AI is enabled and provide online handlers when allowed.
 *
 * Notes:
 * - Without injected handlers the panel renders but returns empty suggestion results.
 */
import { useState, useRef, useEffect } from 'react';
import { Sparkles, X, ChevronRight, RotateCcw, AlertTriangle, Check, Upload, FileText } from 'lucide-react';
import type { TemplateBuilderTemplate, TemplateBuilderForm, FormField } from '../../types/templateTypes';
import { resolveAISuggestionsToFields, type AISuggestedField } from '../../services/formFieldUtils';

// ---------------------------------------------------------------------------
// CanvasAIPanel — AI assistant sidebar for the template builder
//
// Phases covered:
//   9b — NL → template generation (describe a form, get back field suggestions)
//   9d — In-wizard field label / description suggestions
// ---------------------------------------------------------------------------

export type CanvasAISuggestion = AISuggestedField & { hint?: string };

export interface CanvasAIResult {
  suggestions: CanvasAISuggestion[];
  tokensUsed?: number;
}

export interface CanvasAIGenerateRequest {
  prompt: string;
  template: TemplateBuilderTemplate;
  templateType: NonNullable<TemplateBuilderTemplate['templateType']>;
}

export interface CanvasAIParseDocumentRequest {
  file: File;
  template: TemplateBuilderTemplate;
  templateType: NonNullable<TemplateBuilderTemplate['templateType']>;
}

export interface CanvasAIPanelProps {
  template: TemplateBuilderTemplate;
  forms: Map<string, TemplateBuilderForm>;
  onApplySuggestion: (formId: string, fields: Partial<FormField>[]) => void;
  onClose: () => void;
  tokensUsed: number;
  sessionBudget: number;
  onRecordTokens: (n: number) => void;
  onGenerateSuggestions?: (request: CanvasAIGenerateRequest) => Promise<CanvasAIResult>;
  onParseDocument?: (request: CanvasAIParseDocumentRequest) => Promise<CanvasAIResult>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type PanelTab = 'generate' | 'upload';
type PanelMode = 'generate' | 'result' | 'upload-idle' | 'upload-processing' | 'upload-result';


export function CanvasAIPanel({
  template,
  forms,
  onApplySuggestion,
  onClose,
  tokensUsed,
  sessionBudget,
  onRecordTokens,
  onGenerateSuggestions,
  onParseDocument,
}: CanvasAIPanelProps) {
  const [activeTab, setActiveTab] = useState<PanelTab>('generate');
  const [mode, setMode] = useState<PanelMode>('generate');
  const [prompt, setPrompt] = useState('');
  const [targetFormId, setTargetFormId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<CanvasAISuggestion[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [applied, setApplied] = useState(false);
  const [uploadFileName, setUploadFileName] = useState<string | null>(null);
  const [uploadSuggestions, setUploadSuggestions] = useState<CanvasAISuggestion[]>([]);
  const [uploadSelected, setUploadSelected] = useState<Set<number>>(new Set());
  const [uploadApplied, setUploadApplied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const formList = template.formRefs
    .map((r) => ({ id: r.formId, form: forms.get(r.formId) }))
    .filter((f) => f.form);

  useEffect(() => {
    if (formList.length > 0 && !targetFormId) {
      setTargetFormId(formList[0].id);
    }
  }, [formList.length]);

  useEffect(() => {
    if (activeTab === 'generate') textareaRef.current?.focus();
  }, [activeTab]);

  const budgetExceeded = sessionBudget > 0 && tokensUsed >= sessionBudget;
  const budgetWarn = sessionBudget > 0 && tokensUsed / sessionBudget >= 0.8 && !budgetExceeded;
  const budgetPct = sessionBudget > 0 ? Math.min(100, Math.round(tokensUsed / sessionBudget * 100)) : 0;

  const handleGenerate = async () => {
    if (!prompt.trim() || isLoading || budgetExceeded) return;
    if (!template.templateType) {
      console.error('[CanvasAIPanel] Template type is required for AI suggestions');
      return;
    }

    setIsLoading(true);
    try {
      const result = onGenerateSuggestions
        ? await onGenerateSuggestions({ prompt, template, templateType: template.templateType })
        : { suggestions: [] };
      if (result.tokensUsed && result.tokensUsed > 0) onRecordTokens(result.tokensUsed);
      setSuggestions(result.suggestions);
      setSelected(new Set(result.suggestions.map((_, i) => i)));
      setMode('result');
      setApplied(false);
    } catch {
      setSuggestions([]);
      setSelected(new Set());
      setMode('result');
      setApplied(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = () => {
    if (!targetFormId || selected.size === 0) return;
    const selectedSuggestions = [...selected].map((i) => suggestions[i]);
    const resolved = resolveAISuggestionsToFields(selectedSuggestions, template.templateType);
    onApplySuggestion(targetFormId, resolved);
    setApplied(true);
  };

  const handleReset = () => {
    setMode('generate');
    setPrompt('');
    setSuggestions([]);
    setSelected(new Set());
    setApplied(false);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!template.templateType) {
      console.error('[CanvasAIPanel] Template type is required for document parsing');
      return;
    }

    setUploadFileName(file.name);
    setMode('upload-processing');
    setUploadApplied(false);
    try {
      const result = onParseDocument
        ? await onParseDocument({ file, template, templateType: template.templateType })
        : { suggestions: [] };
      if (result.tokensUsed && result.tokensUsed > 0) onRecordTokens(result.tokensUsed);
      setUploadSuggestions(result.suggestions);
      setUploadSelected(new Set(result.suggestions.map((_, i) => i)));
      setMode('upload-result');
    } catch {
      setUploadSuggestions([]);
      setUploadSelected(new Set());
      setMode('upload-result');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleApplyUpload = () => {
    if (!targetFormId || uploadSelected.size === 0) return;
    const selectedSuggestions = [...uploadSelected].map((i) => uploadSuggestions[i]);
    const resolved = resolveAISuggestionsToFields(selectedSuggestions, template.templateType);
    onApplySuggestion(targetFormId, resolved);
    setUploadApplied(true);
  };

  const handleUploadReset = () => {
    setUploadFileName(null);
    setUploadSuggestions([]);
    setUploadSelected(new Set());
    setUploadApplied(false);
    setMode('generate');
  };

  const toggleUploadSelect = (i: number) => {
    setUploadSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const toggleSelect = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const isUploadTab = activeTab === 'upload';

  return (
    <div className="flex flex-col h-full bg-white border-l border-purple-100" style={{ width: 280 }}>
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-3 py-2.5 border-b border-purple-100 bg-purple-50">
        <div className="flex items-center gap-2">
          <Sparkles size={13} className="text-purple-600" />
          <span className="text-xs font-semibold text-purple-800">AI Assistant</span>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600 uppercase tracking-wide">Beta</span>
        </div>
        <button onClick={onClose} className="p-0.5 text-purple-400 hover:text-purple-700 transition-colors">
          <X size={13} />
        </button>
      </div>

      {/* Token usage bar */}
      {sessionBudget > 0 && (
        <div className="shrink-0 px-3 py-1.5 border-b border-purple-100 bg-purple-50/50">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] text-purple-500 font-medium">Session usage</span>
            <span className={`text-[9px] font-semibold ${budgetExceeded ? 'text-red-600' : budgetWarn ? 'text-amber-600' : 'text-purple-500'}`}>
              {tokensUsed.toLocaleString()} / {sessionBudget.toLocaleString()} tokens
            </span>
          </div>
          <div className="h-1 bg-purple-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${budgetExceeded ? 'bg-red-500' : budgetWarn ? 'bg-amber-400' : 'bg-purple-400'}`}
              style={{ width: `${budgetPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="shrink-0 flex border-b border-purple-100">
        <button
          onClick={() => { setActiveTab('generate'); }}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[10px] font-medium transition-colors ${
            activeTab === 'generate' ? 'border-b-2 border-purple-500 text-purple-700' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Sparkles size={10} /> Generate
        </button>
        <button
          onClick={() => { setActiveTab('upload'); }}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[10px] font-medium transition-colors ${
            activeTab === 'upload' ? 'border-b-2 border-purple-500 text-purple-700' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Upload size={10} /> From Document
        </button>
      </div>

      {/* Budget exceeded warning */}
      {budgetExceeded && (
        <div className="shrink-0 mx-3 mt-2 flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle size={12} className="text-red-500 shrink-0 mt-0.5" />
          <p className="text-[10px] text-red-700">Session token budget exhausted. Contact your administrator to increase limits.</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {/* Upload tab */}
        {isUploadTab && (
          <div className="p-3 space-y-3">
            <p className="text-[10px] text-gray-500 leading-relaxed">
              Upload a PDF inspection form or document. AI will extract fields and suggest them for your template.
            </p>
            <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc,.png,.jpg" className="hidden" onChange={handleFileChange} />
            {(mode === 'upload-idle' || mode === 'generate') && !uploadFileName && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={budgetExceeded}
                className="w-full flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed border-purple-200 rounded-lg hover:border-purple-400 hover:bg-purple-50/50 disabled:opacity-40 transition-colors cursor-pointer"
              >
                <Upload size={20} className="text-purple-400" />
                <span className="text-xs text-purple-600 font-medium">Click to upload</span>
                <span className="text-[10px] text-gray-400">PDF, DOCX, PNG, JPG</span>
              </button>
            )}
            {mode === 'upload-processing' && (
              <div className="flex flex-col items-center gap-2 py-6">
                <span className="animate-spin inline-block w-5 h-5 border-2 border-purple-200 border-t-purple-500 rounded-full" />
                <div className="text-xs text-gray-600 font-medium">Parsing document…</div>
                <div className="text-[10px] text-gray-400 truncate max-w-full px-4">{uploadFileName}</div>
              </div>
            )}
            {mode === 'upload-result' && uploadSuggestions.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <FileText size={11} className="text-purple-500" />
                  <span className="text-[10px] text-gray-500 truncate flex-1">{uploadFileName}</span>
                  <button onClick={handleUploadReset} className="text-[10px] text-gray-400 hover:text-gray-600 flex items-center gap-0.5"><RotateCcw size={9} /> New</button>
                </div>
                <div className="space-y-1">
                  {uploadSuggestions.map((s, i) => (
                    <label key={i} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                      uploadSelected.has(i) ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50 border border-gray-200'
                    }`}>
                      <input type="checkbox" checked={uploadSelected.has(i)} onChange={() => toggleUploadSelect(i)} className="w-3 h-3 text-purple-600 rounded-sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-800 truncate">{s.label}</span>
                          {s.required && <span className="text-[9px] text-red-500 font-medium shrink-0">*</span>}
                        </div>
                        <span className="text-[9px] text-gray-400 capitalize">{s.type}</span>
                      </div>
                    </label>
                  ))}
                </div>
                {formList.length > 1 && (
                  <div>
                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Add to form</label>
                    <select value={targetFormId} onChange={(e) => setTargetFormId(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-200 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-purple-300 bg-white">
                      {formList.map((f) => <option key={f.id} value={f.id}>{f.form!.name}</option>)}
                    </select>
                  </div>
                )}
                {uploadApplied ? (
                  <div className="flex items-center gap-1.5 py-2 text-xs text-green-700 font-medium">
                    <Check size={13} className="text-green-500" />
                    {uploadSelected.size} field{uploadSelected.size !== 1 ? 's' : ''} added
                  </div>
                ) : (
                  <button onClick={handleApplyUpload} disabled={uploadSelected.size === 0 || !targetFormId} className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-40 transition-colors">
                    <ChevronRight size={12} /> Add {uploadSelected.size} field{uploadSelected.size !== 1 ? 's' : ''}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Generate mode */}
        {!isUploadTab && mode === 'generate' && (
          <div className="p-3 space-y-3">
            <p className="text-[10px] text-gray-500 leading-relaxed">
              Describe the form or inspection type you want to build. AI will suggest fields to add.
            </p>

            {/* Target form selector */}
            {formList.length > 1 && (
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Add fields to</label>
                <select
                  value={targetFormId}
                  onChange={(e) => setTargetFormId(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-purple-300 bg-white"
                >
                  {formList.map((f) => (
                    <option key={f.id} value={f.id}>{f.form!.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Describe the form</label>
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleGenerate(); }}
                placeholder="e.g. Wildland fire risk inspection form with vegetation clearance and defensible space checks…"
                rows={4}
                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg resize-none focus:outline-hidden focus:ring-1 focus:ring-purple-300"
              />
              <p className="text-[9px] text-gray-400 mt-0.5">⌘↵ to generate</p>
            </div>

            {/* Example prompts */}
            <div>
              <p className="text-[9px] font-medium text-gray-400 mb-1 uppercase tracking-wide">Try an example</p>
              <div className="flex flex-col gap-1">
                {[
                  'Wildland fire risk assessment with vegetation clearance',
                  'Kitchen hood suppression system inspection',
                  'Hydrant flow test with pressure readings',
                  'Sprinkler system annual inspection',
                ].map((ex) => (
                  <button
                    key={ex}
                    onClick={() => setPrompt(ex)}
                    className="text-left text-[10px] text-purple-600 hover:text-purple-800 hover:underline truncate"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={!prompt.trim() || isLoading || budgetExceeded}
              className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-40 transition-colors"
            >
              {isLoading ? (
                <>
                  <span className="animate-spin inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full" />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles size={12} />
                  Generate Fields
                </>
              )}
            </button>
          </div>
        )}

        {/* Result mode */}
        {!isUploadTab && mode === 'result' && (
          <div className="p-3 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-medium text-gray-600">
                {suggestions.length} field{suggestions.length !== 1 ? 's' : ''} suggested
              </p>
              <button
                onClick={handleReset}
                className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600"
              >
                <RotateCcw size={10} />
                New prompt
              </button>
            </div>

            <div className="space-y-1">
              {suggestions.map((s, i) => (
                <label
                  key={i}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                    selected.has(i) ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(i)}
                    onChange={() => toggleSelect(i)}
                    className="w-3 h-3 text-purple-600 rounded-sm"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-800 truncate">{s.label}</span>
                      {s.required && <span className="text-[9px] text-red-500 font-medium shrink-0">*</span>}
                    </div>
                    <span className="text-[9px] text-gray-400 capitalize">{s.type}</span>
                  </div>
                </label>
              ))}
            </div>

            {/* Target form selector */}
            {formList.length > 1 && (
              <div>
                <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Add to form</label>
                <select
                  value={targetFormId}
                  onChange={(e) => setTargetFormId(e.target.value)}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-purple-300 bg-white"
                >
                  {formList.map((f) => (
                    <option key={f.id} value={f.id}>{f.form!.name}</option>
                  ))}
                </select>
              </div>
            )}

            {applied ? (
              <div className="flex items-center gap-1.5 py-2 text-xs text-green-700 font-medium">
                <Check size={13} className="text-green-500" />
                {selected.size} field{selected.size !== 1 ? 's' : ''} added to form
              </div>
            ) : (
              <button
                onClick={handleApply}
                disabled={selected.size === 0 || !targetFormId}
                className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={12} />
                Add {selected.size} field{selected.size !== 1 ? 's' : ''} to form
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


export default CanvasAIPanel;
